import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import {
  AMAP_CHARGING_DESTINATION,
  type ChargingStation,
  type ChargingStationSource,
  type GeoPoint,
  createFallbackChargingStations,
  getRecommendedStation,
  rankChargingStations,
  updateChargingStationRouteFacts
} from "../config/amapChargingDemo";
import {
  createFallbackCarwashShops,
  getRecommendedCarwashShop,
  rankCarwashShops,
  type CarwashShop,
  type CarwashShopSource
} from "../config/amapCarwashDemo";
import {
  createFallbackFuelStations,
  getRecommendedFuelStation,
  rankFuelStations,
  type FuelStation,
  type FuelStationSource
} from "../config/amapFuelDemo";
import {
  createFallbackParcelPickupPoints,
  getRecommendedParcelPickupPoint,
  rankParcelPickupPoints,
  type ParcelPickupPoint,
  type ParcelPointSource
} from "../config/amapParcelDemo";
import {
  createFallbackParcelRouteGeometry,
  createFallbackRouteGeometry,
  createFallbackCarwashRouteGeometry,
  createFallbackFuelRouteGeometry,
  fetchCarwashRouteGeometry,
  fetchFuelRouteGeometry,
  fetchNearbyCarwashShops,
  fetchNearbyChargingStations,
  fetchNearbyFuelStations,
  fetchNearbyParcelPickupPoints,
  fetchParcelRouteGeometry,
  fetchRouteGeometry,
  getDemoVehicleLocationState,
  getFallbackDestinationLocation,
  resolveCurrentVehicleLocation,
  resolveDestinationLocation,
  type RouteGeometry,
  type VehicleLocationState
} from "../services/amapClient";
import type { CabinState } from "../types/domain";
import type { TraditionalDesktopDestinationTarget } from "../components/cabin/TraditionalDesktopMapDestination";

type ServiceFactSource = "amap" | "demo-service" | "control";

export interface CockpitDestinationData {
  name: string;
  subtitle: string;
  location: GeoPoint;
  source: "amap" | "demo";
  error: string | null;
}

export interface CockpitChargingData {
  destination: CockpitDestinationData;
  stations: ChargingStation[];
  source: ChargingStationSource;
  error: string | null;
  isDemoData: boolean;
  recommendedStation: ChargingStation;
  stationCount: number;
}

export interface CockpitCarwashData {
  shops: CarwashShop[];
  source: CarwashShopSource;
  error: string | null;
  isDemoData: boolean;
  recommendedShop: CarwashShop;
  shopCount: number;
}

export interface CockpitParcelData {
  pendingCount: number;
  pickupPoint: ParcelPickupPoint;
  pickupPoints: ParcelPickupPoint[];
  source: ParcelPointSource;
  error: string | null;
  isDemoData: boolean;
  pointCount: number;
  onRoute: boolean;
  detourKm: number;
  eta: string;
}

export interface CockpitFuelData {
  stations: FuelStation[];
  source: FuelStationSource;
  error: string | null;
  isDemoData: boolean;
  recommendedStation: FuelStation;
  stationCount: number;
}

export interface CockpitData {
  carwash: CockpitCarwashData;
  charging: CockpitChargingData;
  fuel: CockpitFuelData;
  home: {
    name: "家";
    location: GeoPoint;
    source: VehicleLocationState["locationSource"];
  };
  parcel: CockpitParcelData;
  traffic: {
    mode: CabinState["traffic"];
    label: string;
    source: ServiceFactSource;
  };
  vehicle: VehicleLocationState & {
    refreshLocation: () => void;
  };
  weather: {
    mode: CabinState["weather"];
    forecastGoodDays: number;
    label: string;
    source: ServiceFactSource;
  };
}

const CockpitDataContext = createContext<CockpitData | null>(null);

export function CockpitDataProvider({
  children,
  value
}: {
  children: ReactNode;
  value: CockpitData;
}) {
  return (
    <CockpitDataContext.Provider value={value}>
      {children}
    </CockpitDataContext.Provider>
  );
}

export function useCockpitData() {
  const value = useContext(CockpitDataContext);

  if (!value) {
    throw new Error("useCockpitData must be used inside CockpitDataProvider");
  }

  return value;
}

export function useCockpitDataModel(state: CabinState): CockpitData {
  const destination = useDestinationData(state.destinationName);
  const vehicle = useVehicleLocationData();
  const chargingLookup = useChargingStationsData(destination.location, vehicle.vehicleLocation);
  const serviceSearchCenter = destination.location;
  const carwashLookup = useCarwashShopsData(serviceSearchCenter, state.carwashCoupon);
  const fuelLookup = useFuelStationsData(serviceSearchCenter, state.fuelLevel <= 35 || state.fuelRangeKm <= 260);
  const home = useMemo(
    () => ({
      name: "家" as const,
      location: vehicle.vehicleLocation,
      source: vehicle.locationSource
    }),
    [vehicle.locationSource, vehicle.vehicleLocation]
  );
  const parcelLookup = useParcelPickupPointsData(home.location, state.parcelPending ? 2 : 0);
  const recommendedStation = getRecommendedStation(chargingLookup.stations);
  const recommendedCarwashShop = getRecommendedCarwashShop(carwashLookup.shops);
  const recommendedFuelStation = getRecommendedFuelStation(fuelLookup.stations);
  const recommendedPickupPoint = getRecommendedParcelPickupPoint(parcelLookup.pickupPoints);

  return useMemo(
    () => ({
      carwash: {
        shops: carwashLookup.shops,
        source: carwashLookup.source,
        error: carwashLookup.error,
        isDemoData: carwashLookup.source !== "amap",
        recommendedShop: recommendedCarwashShop,
        shopCount: carwashLookup.shops.length
      },
      charging: {
        destination,
        stations: chargingLookup.stations,
        source: chargingLookup.source,
        error: chargingLookup.error,
        isDemoData: chargingLookup.source !== "amap",
        recommendedStation,
        stationCount: chargingLookup.stations.length
      },
      fuel: {
        stations: fuelLookup.stations,
        source: fuelLookup.source,
        error: fuelLookup.error,
        isDemoData: fuelLookup.source !== "amap",
        recommendedStation: recommendedFuelStation,
        stationCount: fuelLookup.stations.length
      },
      home,
      parcel: buildParcelData(state, parcelLookup, recommendedPickupPoint),
      traffic: {
        mode: state.traffic,
        label: getTrafficLabel(state.traffic),
        source: "control"
      },
      vehicle,
      weather: {
        mode: state.weather,
        forecastGoodDays: state.forecastGoodDays,
        label: getWeatherLabel(state.weather, state.forecastGoodDays),
        source: "control"
      }
    }),
    [
      chargingLookup.error,
      chargingLookup.source,
      chargingLookup.stations,
      destination,
      carwashLookup.error,
      carwashLookup.shops,
      carwashLookup.source,
      fuelLookup.error,
      fuelLookup.source,
      fuelLookup.stations,
      parcelLookup.error,
      parcelLookup.pickupPoints,
      parcelLookup.source,
      recommendedPickupPoint,
      recommendedCarwashShop,
      recommendedFuelStation,
      recommendedStation,
      state,
      home,
      vehicle
    ]
  );
}

export function applyCockpitDataToCabinState(state: CabinState, cockpitData: CockpitData): CabinState {
  const chargingPatch = getChargingStatePatch(state, cockpitData.charging);

  return {
    ...state,
    ...chargingPatch
  };
}

export function useCockpitRouteGeometry({
  originOverride,
  selectedCarwashShop,
  selectedFuelStation,
  selectedParcelPoint,
  selectedStation,
  target
}: {
  originOverride?: GeoPoint;
  selectedCarwashShop?: CarwashShop;
  selectedFuelStation?: FuelStation;
  selectedParcelPoint?: ParcelPickupPoint;
  selectedStation: ChargingStation;
  target: TraditionalDesktopDestinationTarget;
}) {
  const {
    charging: { destination },
    vehicle: { vehicleLocation }
  } = useCockpitData();
  const routeOrigin = originOverride ?? vehicleLocation;
  const routeParcelPoint = useMemo(
    () => selectedParcelPoint ?? createFallbackParcelPickupPoints(routeOrigin)[0],
    [routeOrigin, selectedParcelPoint]
  );
  const routeCarwashShop = useMemo(
    () => selectedCarwashShop ?? createFallbackCarwashShops(routeOrigin)[0],
    [routeOrigin, selectedCarwashShop]
  );
  const routeFuelStation = useMemo(
    () => selectedFuelStation ?? createFallbackFuelStations(routeOrigin)[0],
    [routeOrigin, selectedFuelStation]
  );
  const fallbackGeometry = useMemo(
    () => target === "parcel"
      ? createFallbackParcelRouteGeometry(routeParcelPoint, routeOrigin)
      : target === "carwash"
        ? createFallbackCarwashRouteGeometry(routeCarwashShop, routeOrigin)
        : target === "refuel"
          ? createFallbackFuelRouteGeometry(routeFuelStation, routeOrigin)
      : createFallbackRouteGeometry(destination.location, selectedStation, target, routeOrigin),
    [destination.location, routeCarwashShop, routeFuelStation, routeParcelPoint, selectedStation, target, routeOrigin]
  );
  const [routeGeometry, setRouteGeometry] = useState<RouteGeometry>(fallbackGeometry);

  useEffect(() => {
    let cancelled = false;
    const webServiceKey = import.meta.env.VITE_AMAP_WEB_SERVICE_KEY;

    setRouteGeometry({
      ...fallbackGeometry,
      status: webServiceKey ? "loading" : "fallback"
    });

    if (!webServiceKey) {
      return undefined;
    }

    const routeRequest = target === "parcel"
      ? fetchParcelRouteGeometry({
          selectedPickupPoint: routeParcelPoint,
          vehicleLocation: routeOrigin,
          webServiceKey
        })
      : target === "carwash"
        ? fetchCarwashRouteGeometry({
            selectedCarwashShop: routeCarwashShop,
            vehicleLocation: routeOrigin,
            webServiceKey
          })
        : target === "refuel"
          ? fetchFuelRouteGeometry({
              selectedFuelStation: routeFuelStation,
              vehicleLocation: routeOrigin,
              webServiceKey
            })
        : fetchRouteGeometry({
            destinationLocation: destination.location,
            selectedStation,
            target,
            vehicleLocation: routeOrigin,
            webServiceKey
          });

    routeRequest
      .then((nextGeometry) => {
        if (!cancelled) {
          setRouteGeometry(nextGeometry);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRouteGeometry(fallbackGeometry);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [destination.location, fallbackGeometry, routeCarwashShop, routeFuelStation, routeParcelPoint, selectedStation, target, routeOrigin]);

  return routeGeometry;
}

function useDestinationData(destinationName: string): CockpitDestinationData {
  const requestedName = destinationName.trim() || AMAP_CHARGING_DESTINATION.name;
  const fallbackDestination = useMemo(
    () => getFallbackDestinationLocation(requestedName),
    [requestedName]
  );
  const [destination, setDestination] = useState<CockpitDestinationData>(() => ({
    ...fallbackDestination,
    subtitle: fallbackDestination.source === "amap" ? "高德目的地" : "演示目的地",
    error: null
  }));

  useEffect(() => {
    let cancelled = false;
    const webServiceKey = import.meta.env.VITE_AMAP_WEB_SERVICE_KEY;
    const fallbackData: CockpitDestinationData = {
      ...fallbackDestination,
      subtitle: "演示目的地",
      error: webServiceKey ? null : "missing-web-service-key"
    };

    setDestination(fallbackData);

    if (!webServiceKey) {
      return undefined;
    }

    resolveDestinationLocation(webServiceKey, requestedName)
      .then((nextDestination) => {
        if (!cancelled) {
          setDestination({
            ...nextDestination,
            subtitle: "高德目的地",
            error: null
          });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDestination({
            ...fallbackData,
            error: error instanceof Error ? error.message : "amap-destination-lookup-failed"
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fallbackDestination, requestedName]);

  return destination;
}

function useChargingStationsData(searchCenter: GeoPoint, vehicleLocation: GeoPoint) {
  const fallbackStations = useMemo(
    () => rankChargingStations(
      createFallbackChargingStations(searchCenter).map((station) =>
        updateChargingStationRouteFacts(station, vehicleLocation, searchCenter)
      )
    ),
    [searchCenter, vehicleLocation]
  );
  const [stations, setStations] = useState<ChargingStation[]>(fallbackStations);
  const [source, setSource] = useState<ChargingStationSource>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setStations(fallbackStations);
  }, [fallbackStations]);

  useEffect(() => {
    let cancelled = false;

    async function lookupStations() {
      const webServiceKey = import.meta.env.VITE_AMAP_WEB_SERVICE_KEY;

      if (!webServiceKey) {
        setStations(fallbackStations);
        setSource("demo");
        setError("missing-web-service-key");
        return;
      }

      setSource("loading");

      try {
        const nextStations = await fetchNearbyChargingStations(webServiceKey, searchCenter, vehicleLocation);

        if (!cancelled) {
          setStations(nextStations);
          setSource("amap");
          setError(null);
        }
      } catch (lookupError) {
        if (!cancelled) {
          setStations(fallbackStations);
          setSource("demo");
          setError(lookupError instanceof Error ? lookupError.message : "amap-lookup-failed");
        }
      }
    }

    lookupStations();

    return () => {
      cancelled = true;
    };
  }, [fallbackStations, searchCenter, vehicleLocation]);

  return useMemo(
    () => ({
      stations,
      source,
      error
    }),
    [error, source, stations]
  );
}

function useCarwashShopsData(searchCenter: GeoPoint, enabled: boolean) {
  const fallbackShops = useMemo(
    () => rankCarwashShops(createFallbackCarwashShops(searchCenter)),
    [searchCenter]
  );
  const [shops, setShops] = useState<CarwashShop[]>(fallbackShops);
  const [source, setSource] = useState<CarwashShopSource>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setShops(fallbackShops);
  }, [fallbackShops]);

  useEffect(() => {
    let cancelled = false;

    async function lookupCarwashShops() {
      const webServiceKey = import.meta.env.VITE_AMAP_WEB_SERVICE_KEY;

      if (!enabled) {
        setShops(fallbackShops);
        setSource("demo");
        setError(null);
        return;
      }

      if (!webServiceKey) {
        setShops(fallbackShops);
        setSource("demo");
        setError("missing-web-service-key");
        return;
      }

      setSource("loading");

      try {
        const nextShops = await fetchNearbyCarwashShops(webServiceKey, searchCenter);

        if (!cancelled) {
          setShops(nextShops);
          setSource("amap");
          setError(null);
        }
      } catch (lookupError) {
        if (!cancelled) {
          setShops(fallbackShops);
          setSource("demo");
          setError(lookupError instanceof Error ? lookupError.message : "amap-carwash-lookup-failed");
        }
      }
    }

    lookupCarwashShops();

    return () => {
      cancelled = true;
    };
  }, [enabled, fallbackShops, searchCenter]);

  return useMemo(
    () => ({
      shops,
      source,
      error
    }),
    [error, shops, source]
  );
}

function useFuelStationsData(searchCenter: GeoPoint, enabled: boolean) {
  const fallbackStations = useMemo(
    () => rankFuelStations(createFallbackFuelStations(searchCenter)),
    [searchCenter]
  );
  const [stations, setStations] = useState<FuelStation[]>(fallbackStations);
  const [source, setSource] = useState<FuelStationSource>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setStations(fallbackStations);
  }, [fallbackStations]);

  useEffect(() => {
    let cancelled = false;

    async function lookupFuelStations() {
      const webServiceKey = import.meta.env.VITE_AMAP_WEB_SERVICE_KEY;

      if (!enabled) {
        setStations(fallbackStations);
        setSource("demo");
        setError(null);
        return;
      }

      if (!webServiceKey) {
        setStations(fallbackStations);
        setSource("demo");
        setError("missing-web-service-key");
        return;
      }

      setSource("loading");

      try {
        const nextStations = await fetchNearbyFuelStations(webServiceKey, searchCenter);

        if (!cancelled) {
          setStations(nextStations);
          setSource("amap");
          setError(null);
        }
      } catch (lookupError) {
        if (!cancelled) {
          setStations(fallbackStations);
          setSource("demo");
          setError(lookupError instanceof Error ? lookupError.message : "amap-fuel-lookup-failed");
        }
      }
    }

    lookupFuelStations();

    return () => {
      cancelled = true;
    };
  }, [enabled, fallbackStations, searchCenter]);

  return useMemo(
    () => ({
      stations,
      source,
      error
    }),
    [error, source, stations]
  );
}

function useParcelPickupPointsData(vehicleLocation: GeoPoint, pendingCount: number) {
  const fallbackPoints = useMemo(
    () => rankParcelPickupPoints(createFallbackParcelPickupPoints(vehicleLocation, Math.max(1, pendingCount || 2))).slice(0, 1),
    [pendingCount, vehicleLocation]
  );
  const [pickupPoints, setPickupPoints] = useState<ParcelPickupPoint[]>(fallbackPoints);
  const [source, setSource] = useState<ParcelPointSource>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPickupPoints(fallbackPoints);
  }, [fallbackPoints]);

  useEffect(() => {
    let cancelled = false;

    async function lookupPickupPoints() {
      const webServiceKey = import.meta.env.VITE_AMAP_WEB_SERVICE_KEY;

      if (!webServiceKey) {
        setPickupPoints(fallbackPoints);
        setSource("demo");
        setError("missing-web-service-key");
        return;
      }

      setSource("loading");

      try {
        const nextPoints = await fetchNearbyParcelPickupPoints(
          webServiceKey,
          vehicleLocation,
          Math.max(1, pendingCount || 2)
        );

        if (!cancelled) {
          setPickupPoints(nextPoints.slice(0, 1));
          setSource("amap");
          setError(null);
        }
      } catch (lookupError) {
        if (!cancelled) {
          setPickupPoints(fallbackPoints);
          setSource("demo");
          setError(lookupError instanceof Error ? lookupError.message : "amap-parcel-lookup-failed");
        }
      }
    }

    lookupPickupPoints();

    return () => {
      cancelled = true;
    };
  }, [fallbackPoints, pendingCount, vehicleLocation]);

  return useMemo(
    () => ({
      pickupPoints,
      source,
      error
    }),
    [error, pickupPoints, source]
  );
}

function useVehicleLocationData(): CockpitData["vehicle"] {
  const requestIdRef = useState(() => ({ current: 0 }))[0];
  const [state, setState] = useState<VehicleLocationState>(() => ({
    ...getDemoVehicleLocationState(),
    locationStatus: "locating"
  }));

  const refreshLocation = useCallback(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setState((current) => ({
      ...current,
      locationStatus: "locating",
      locationError: undefined
    }));

    resolveCurrentVehicleLocation()
      .then((nextState) => {
        if (requestIdRef.current === requestId) {
          setState(nextState);
        }
      })
      .catch((error) => {
        if (requestIdRef.current !== requestId) {
          return;
        }

        const locationError = error instanceof Error ? error.message : "location-failed";

        setState((current) => ({
          ...(current.locationStatus === "live" ? current : getDemoVehicleLocationState()),
          locationError,
          locationStatus: current.locationStatus === "live" ? "live" : "demo"
        }));
      });
  }, [requestIdRef]);

  useEffect(() => {
    refreshLocation();
  }, [refreshLocation]);

  return useMemo(
    () => ({
      ...state,
      refreshLocation
    }),
    [refreshLocation, state]
  );
}

function getChargingStatePatch(state: CabinState, charging: CockpitChargingData): Partial<CabinState> {
  if (charging.source === "loading") {
    return {};
  }

  return {
    chargerAvailability: Math.max(state.chargerAvailability, charging.recommendedStation.availableStalls),
    chargerPrice: Math.min(state.chargerPrice, charging.recommendedStation.pricePerKwh)
  };
}

function buildParcelData(
  state: CabinState,
  lookup: {
    pickupPoints: ParcelPickupPoint[];
    source: ParcelPointSource;
    error: string | null;
  },
  recommendedPickupPoint: ParcelPickupPoint
): CockpitParcelData {
  const pendingCount = state.parcelPending ? 2 : 0;
  const onRoute = Boolean(state.parcelPending && state.parcelOnRoute);
  const pickupPoint = {
    ...recommendedPickupPoint,
    pendingCount: Math.max(1, pendingCount || recommendedPickupPoint.pendingCount)
  };

  return {
    pendingCount,
    pickupPoint,
    pickupPoints: lookup.pickupPoints.map((point) => ({
      ...point,
      pendingCount: Math.max(1, pendingCount || point.pendingCount)
    })),
    source: lookup.source,
    error: lookup.error,
    isDemoData: lookup.source !== "amap",
    pointCount: lookup.pickupPoints.length,
    onRoute,
    detourKm: onRoute ? pickupPoint.detourKm : 0,
    eta: onRoute ? pickupPoint.eta : "--:--"
  };
}

function getTrafficLabel(traffic: CabinState["traffic"]) {
  if (traffic === "heavy") {
    return "拥堵";
  }

  if (traffic === "medium") {
    return "适中";
  }

  return "顺畅";
}

function getWeatherLabel(weather: CabinState["weather"], forecastGoodDays: number) {
  if (weather === "rainy") {
    return "雨";
  }

  if (weather === "cloudy") {
    return `多云 · 未来${forecastGoodDays}天较好`;
  }

  return `晴 · 未来${forecastGoodDays}天较好`;
}
